import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Menu, MenuItem,
  Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TableSortLabel, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import { Search, Filter, Plus, X, Save, Star, Download, Upload, Trash2, Share2 } from "lucide-react";
import { GlassCard, T, TYPE, formatDate, formatRelative, labelSx, primaryButtonSx, ghostButtonSx } from "../user/complaints/shared";
import { useSocketEvent } from "../helpdesk/socket";
import { pm, errorMessage, saveBlob } from "./api";
import { usePm } from "./PmContext";
import { TypeIcon, PriorityIcon, StatusPill, Assignee, Labels, Points, IssueKey } from "./ui";
import { PersonPicker, PrioritySelect, Select, fieldSx } from "./inputs";
import IssueDetail, { IssueDetailModal } from "./IssueDetail";

// Filter vocabulary understood by the server's buildFilter.
const FIELDS = [
  { key: "type", label: "Type", kind: "type" }, { key: "status", label: "Status", kind: "status" }, { key: "statusCategory", label: "Status category", kind: "category" },
  { key: "priority", label: "Priority", kind: "priority" }, { key: "priorityLevel", label: "Priority level", kind: "number" },
  { key: "assignee", label: "Assignee", kind: "person" }, { key: "reporter", label: "Reporter", kind: "person" }, { key: "watcher", label: "Watcher", kind: "person" },
  { key: "sprint", label: "Sprint", kind: "sprint" }, { key: "labels", label: "Label", kind: "text" }, { key: "storyPoints", label: "Story points", kind: "number" },
  { key: "due", label: "Due date", kind: "date" }, { key: "created", label: "Created", kind: "date" }, { key: "updated", label: "Updated", kind: "date" }, { key: "resolved", label: "Resolved", kind: "date" },
  { key: "resolution", label: "Resolution", kind: "text" }, { key: "summary", label: "Summary", kind: "text" },
  { key: "blocked", label: "Blocked", kind: "bool" }, { key: "flagged", label: "Flagged", kind: "bool" }, { key: "daysInStatus", label: "Days in status", kind: "number" },
  { key: "epic", label: "Epic (id)", kind: "text" }, { key: "parent", label: "Parent (id)", kind: "text" }, { key: "component", label: "Component (id)", kind: "text" }, { key: "fixVersion", label: "Fix version (id)", kind: "text" },
];
const OPS = {
  default: [["eq", "is"], ["neq", "is not"], ["in", "is any of"], ["exists", "is set"], ["not_exists", "is empty"]],
  number: [["eq", "="], ["gt", ">"], ["gte", "≥"], ["lt", "<"], ["lte", "≤"], ["exists", "is set"], ["not_exists", "is empty"]],
  date: [["gte", "on or after"], ["lte", "on or before"], ["exists", "is set"], ["not_exists", "is empty"]],
  text: [["eq", "is"], ["contains", "contains"], ["neq", "is not"], ["exists", "is set"], ["not_exists", "is empty"]],
};
const COLUMNS = [["type", "T"], ["key", "Key"], ["summary", "Summary"], ["status", "Status"], ["priority", "P"], ["assignee", "Assignee"], ["points", "Pts"], ["due", "Due"], ["updated", "Updated"]];

const ValueInput = ({ field, cond, onChange, ctx, sprints }) => {
  const f = FIELDS.find((x) => x.key === cond.field) || {};
  if (["exists", "not_exists"].includes(cond.op)) return null;
  const multi = cond.op === "in";
  const v = cond.value;
  const opts = {
    type: ctx.issueTypes.map((t) => ({ value: t.key || t._id, label: t.name })),
    status: ctx.statuses.map((s) => ({ value: s._id, label: s.name })),
    category: [["todo", "To do"], ["in_progress", "In progress"], ["done", "Done"]].map(([value, label]) => ({ value, label })),
    bool: [{ value: true, label: "Yes" }, { value: false, label: "No" }],
    priority: ctx.priorities.map((p) => ({ value: p._id, label: p.name })),
    person: [{ value: "me", label: "Me" }, { value: "none", label: "Unassigned / none" }, ...ctx.people.map((p) => ({ value: String(p.id), label: p.name }))],
    sprint: [{ value: "active", label: "Active sprint(s)" }, { value: "open", label: "Open sprints" }, { value: "none", label: "Backlog (no sprint)" }, ...sprints.map((s) => ({ value: s._id, label: s.name }))],
  }[f.kind];
  if (opts) {
    return (
      <TextField select size="small" SelectProps={{ multiple: multi }} value={multi ? [].concat(v || []) : v ?? ""} onChange={(e) => onChange(e.target.value)} sx={{ ...fieldSx, minWidth: 180 }}>
        {opts.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>
    );
  }
  if (f.kind === "date") {
    return <TextField size="small" placeholder="YYYY-MM-DD or -7d / today" value={v ?? ""} onChange={(e) => onChange(e.target.value)} sx={{ ...fieldSx, width: 190 }} />;
  }
  return <TextField size="small" type={f.kind === "number" ? "number" : "text"} value={v ?? ""} onChange={(e) => onChange(f.kind === "number" ? Number(e.target.value) : e.target.value)} sx={{ ...fieldSx, width: 170 }} />;
};

export const FilterBuilder = ({ query, onChange, sprints }) => {
  const ctx = usePm();
  const set = (group, list) => onChange({ ...query, [group]: list });
  const groups = [["all", "Match ALL of"], ["any", "…and ANY of"], ["none", "…and NONE of (NOT)"]];
  return (
    <GlassCard sx={{ p: 1.5, mb: 2, boxShadow: "none" }}>
      {groups.map(([g, title]) => (
        <Box key={g} sx={{ mb: 1 }}>
          <Typography sx={{ ...labelSx, fontSize: "0.66rem", mb: 0.5 }}>{title}</Typography>
          {(query[g] || []).map((c, i) => {
            const kind = FIELDS.find((x) => x.key === c.field)?.kind;
            const ops = OPS[kind] || OPS[["number", "date", "text"].includes(kind) ? kind : "default"];
            return (
              <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
                <TextField select size="small" value={c.field} onChange={(e) => set(g, query[g].map((x, j) => (j === i ? { field: e.target.value, op: "eq", value: "" } : x)))} sx={{ ...fieldSx, minWidth: 150 }}>
                  {FIELDS.map((f) => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
                  {ctx.customFields.map((f) => <MenuItem key={f.key} value={`cf.${f.key}`}>{f.label}</MenuItem>)}
                </TextField>
                <TextField select size="small" value={c.op} onChange={(e) => set(g, query[g].map((x, j) => (j === i ? { ...x, op: e.target.value, value: e.target.value === "in" ? [].concat(x.value || []) : Array.isArray(x.value) ? x.value[0] : x.value } : x)))} sx={{ ...fieldSx, minWidth: 120 }}>
                  {ops.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                </TextField>
                <ValueInput cond={c} ctx={ctx} sprints={sprints} onChange={(val) => set(g, query[g].map((x, j) => (j === i ? { ...x, value: val } : x)))} />
                <IconButton size="small" onClick={() => set(g, query[g].filter((_, j) => j !== i))}><X size={14} /></IconButton>
              </Stack>
            );
          })}
          <Button size="small" startIcon={<Plus size={13} />} onClick={() => set(g, [...(query[g] || []), { field: "status", op: "eq", value: "" }])}>Add condition</Button>
        </Box>
      ))}
    </GlassCard>
  );
};

const IMPORT_FIELDS = ["key", "summary", "description", "type", "priority", "assignee", "reporter", "status", "labels", "sprint", "storyPoints", "dueDate", "startDate", "epic", "parent", "components", "fixVersion", "originalEstimate"];
const guess = (h) => {
  const s = String(h).toLowerCase().replace(/[^a-z]/g, "");
  const map = { issuekey: "key", key: "key", summary: "summary", title: "summary", description: "description", issuetype: "type", type: "type", priority: "priority", assignee: "assignee", reporter: "reporter", status: "status", labels: "labels", label: "labels", sprint: "sprint", storypoints: "storyPoints", points: "storyPoints", duedate: "dueDate", due: "dueDate", startdate: "startDate", epic: "epic", epiclink: "epic", parent: "parent", components: "components", component: "components", fixversion: "fixVersion", fixversions: "fixVersion", originalestimate: "originalEstimate", estimate: "originalEstimate" };
  return map[s] || "";
};

export const ImportDialog = ({ open, onClose, project, onDone }) => {
  const ctx = usePm();
  const [rows, setRows] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setRows(null);
      setPreview(null);
      setError("");
    }
  }, [open]);
  const read = async (file) => {
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: true });
      const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "", raw: false });
      if (!data.length) throw new Error("The file has no rows");
      const hs = Object.keys(data[0]);
      setHeaders(hs);
      setMapping(Object.fromEntries(hs.map((h) => [h, guess(h)])));
      setRows(data);
    } catch (err) {
      setError(err.message || "Could not read the file");
    }
  };
  const mapped = () => rows.map((r) => Object.fromEntries(Object.entries(mapping).filter(([, f]) => f).map(([h, f]) => [f, r[h]])));
  const run = async (dryRun) => {
    setBusy(true);
    setError("");
    try {
      const r = await pm.importIssues(project._id, mapped(), dryRun);
      setPreview(r);
      if (!dryRun) {
        ctx.notify(`${r.valid} issues imported`);
        onDone();
        onClose();
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Import issues into {project?.key}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!rows ? (
          <Box sx={{ textAlign: "center", py: 4, border: `2px dashed ${T.border}`, borderRadius: "10px" }}>
            <Upload size={28} color={T.inkMuted} />
            <Typography sx={{ fontSize: TYPE.small, my: 1 }}>Upload a CSV or Excel file (first sheet, header row). Up to 2000 rows.</Typography>
            <Button variant="contained" component="label" sx={primaryButtonSx}>Choose file<input hidden type="file" accept=".csv,.xlsx,.xls" onChange={(e) => e.target.files[0] && read(e.target.files[0])} /></Button>
            <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mt: 1.5 }}>Assignee/reporter match by email or name; type, priority, status, sprint and versions by name. Rows can point to an epic or parent by the value in a "key" column.</Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: TYPE.small, mb: 1 }}>{rows.length} rows. Map columns to fields:</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1, mb: 2 }}>
              {headers.map((h) => (
                <Stack key={h} direction="row" spacing={1} alignItems="center">
                  <Typography noWrap sx={{ fontSize: TYPE.small, width: 160 }} title={h}>{h}</Typography>
                  <TextField select size="small" fullWidth value={mapping[h] || ""} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })} sx={fieldSx}>
                    <MenuItem value=""><em>Skip</em></MenuItem>
                    {IMPORT_FIELDS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </TextField>
                </Stack>
              ))}
            </Box>
            {preview && (
              <Box>
                <Alert severity={preview.invalid ? "warning" : "success"} sx={{ mb: 1 }}>{preview.valid} valid · {preview.invalid} with problems{preview.invalid ? " (they will be skipped)" : ""}</Alert>
                {preview.results.filter((r) => !r.ok).slice(0, 50).map((r) => <Typography key={r.row} sx={{ fontSize: TYPE.micro, color: "#B42318" }}>Row {r.row}: {r.errors.join("; ")}</Typography>)}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" sx={ghostButtonSx} onClick={onClose}>Cancel</Button>
        {rows && <Button variant="outlined" sx={ghostButtonSx} disabled={busy || !Object.values(mapping).includes("summary")} onClick={() => run(true)}>Validate</Button>}
        {rows && <Button variant="contained" sx={primaryButtonSx} disabled={busy || !preview || !preview.valid} onClick={() => run(false)}>{busy ? "Importing…" : `Import ${preview?.valid || ""}`}</Button>}
      </DialogActions>
    </Dialog>
  );
};

const BulkBar = ({ selected, clear, onDone, sprints }) => {
  const ctx = usePm();
  const [op, setOp] = useState(null);
  const [params, setParams] = useState({});
  const run = async () => {
    try {
      const r = await pm.bulk(selected, op, params);
      ctx.notify(`${r.succeeded} updated${r.failed ? `, ${r.failed} failed (${r.results.find((x) => !x.ok)?.error})` : ""}`, r.failed ? "warning" : "success");
      setOp(null);
      setParams({});
      clear();
      onDone();
    } catch (err) {
      ctx.fail(err);
    }
  };
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, p: 1, bgcolor: T.brandSoft, borderRadius: "8px" }} flexWrap="wrap" useFlexGap>
      <Typography sx={{ fontSize: TYPE.small, fontWeight: 700 }}>{selected.length} selected</Typography>
      <TextField select size="small" value={op || ""} onChange={(e) => { setOp(e.target.value); setParams({}); }} label="Bulk action" sx={{ ...fieldSx, minWidth: 160 }}>
        {[["assign", "Assign"], ["priority", "Change priority"], ["sprint", "Move to sprint"], ["labels", "Add labels"], ["transition", "Change status"], ["delete", "Delete"]].map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
      </TextField>
      {op === "assign" && <Box sx={{ minWidth: 200 }}><PersonPicker value={params.assigneeId} onChange={(id) => setParams({ assigneeId: id })} /></Box>}
      {op === "priority" && <Box sx={{ minWidth: 160 }}><PrioritySelect value={params.priorityId} onChange={(id) => setParams({ priorityId: id })} /></Box>}
      {op === "sprint" && <Box sx={{ minWidth: 180 }}><Select label="Sprint" value={params.sprintId} onChange={(id) => setParams({ sprintId: id })} allowEmpty emptyLabel="Backlog" options={sprints.map((s) => ({ value: s._id, label: s.name }))} /></Box>}
      {op === "labels" && <TextField size="small" label="Labels (comma separated)" value={(params.add || []).join(",")} onChange={(e) => setParams({ add: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} sx={fieldSx} />}
      {op === "transition" && (
        <>
          <Box sx={{ minWidth: 160 }}><Select label="Status" value={params.statusId} onChange={(id) => setParams((p) => ({ ...p, statusId: id }))} options={ctx.statuses.map((s) => ({ value: s._id, label: s.name }))} /></Box>
          <Box sx={{ minWidth: 150 }}><Select label="Resolution" value={params.resolution} onChange={(r) => setParams((p) => ({ ...p, resolution: r }))} allowEmpty options={ctx.settings.resolutions.map((r) => ({ value: r, label: r }))} /></Box>
        </>
      )}
      <Button size="small" variant="contained" color={op === "delete" ? "error" : "primary"} disabled={!op} onClick={() => (op !== "delete" || window.confirm(`Delete ${selected.length} issues?`)) && run()}>Apply</Button>
      <Button size="small" onClick={clear}>Clear</Button>
    </Stack>
  );
};

/**
 * Issue search. `project` given → scoped to it (project Issues tab); otherwise
 * searches every accessible project (/pm/issues).
 */
const IssueNavigator = ({ project, version }) => {
  const ctx = usePm();
  const [params, setParams] = useSearchParams();
  const initial = useMemo(() => {
    try {
      if (params.get("q")) return JSON.parse(params.get("q"));
    } catch {
      /* ignore malformed link */
    }
    const all = [];
    if (params.get("assignee")) all.push({ field: "assignee", op: "eq", value: params.get("assignee") });
    return { all, any: [], none: [] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [query, setQuery] = useState(initial);
  const [text, setText] = useState(params.get("text") || "");
  const conditionCount = (q) => (q.all?.length || 0) + (q.any?.length || 0) + (q.none?.length || 0);
  const [showFilters, setShowFilters] = useState(Boolean(conditionCount(initial)));
  const [sort, setSort] = useState({ by: "updated", order: "desc" });
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [openIssue, setOpenIssue] = useState(null);
  const [filters, setFilters] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [sprints, setSprints] = useState([]);
  const [menu, setMenu] = useState(null);
  const [preset, setPreset] = useState("all");

  const effective = useMemo(() => {
    const presets = {
      all: [], mine: [{ field: "assignee", op: "eq", value: "me" }], reported: [{ field: "reporter", op: "eq", value: "me" }],
      open: [{ field: "statusCategory", op: "neq", value: "done" }], done: [{ field: "statusCategory", op: "eq", value: "done" }],
      overdue: [{ field: "statusCategory", op: "neq", value: "done" }, { field: "due", op: "lte", value: "today" }], recent: [{ field: "updated", op: "gte", value: "-7d" }],
    };
    const clean = (list) => (list || []).filter((c) => c.field && c.op && (["exists", "not_exists"].includes(c.op) || (c.value !== "" && c.value !== undefined && !(Array.isArray(c.value) && !c.value.length))));
    return { all: [...presets[preset], ...clean(query.all)], any: clean(query.any), none: clean(query.none), text: text || undefined, projectIds: project ? [project._id] : undefined };
  }, [query, text, preset, project]);

  const load = useCallback(async () => {
    try {
      const r = project
        ? await pm.projectIssues(project._id, effective, { page: page + 1, limit, sort: sort.by, order: sort.order })
        : await pm.searchIssues(effective, { page: page + 1, limit, sort: sort.by, order: sort.order });
      setRows(r.data);
      setTotal(r.pagination.total);
      if (r.projects) setProjects(r.projects);
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [effective, page, limit, sort, project]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, version]);
  useSocketEvent(["issue.created", "issue.updated", "issue.deleted"], () => load());
  useEffect(() => {
    pm.filters(project?._id).then(setFilters).catch(() => {});
    if (project) pm.sprints(project._id).then(setSprints).catch(() => {});
  }, [project]);
  // keep the query in the URL so it can be shared
  useEffect(() => {
    const n = new URLSearchParams(params);
    if (conditionCount(query)) n.set("q", JSON.stringify(query));
    else n.delete("q");
    if (text) n.set("text", text);
    else n.delete("text");
    n.delete("assignee");
    setParams(n, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, text]);

  const applyFilter = (f) => {
    setActiveFilter(f);
    setPreset("all");
    setQuery({ all: f.query.all || [], any: f.query.any || [], none: f.query.none || [] });
    setText(f.query.text || "");
    if (f.query.sort?.by) setSort({ by: f.query.sort.by, order: f.query.sort.order === "asc" ? "asc" : "desc" });
    setPage(0);
    setShowFilters(true);
    setMenu(null);
  };
  const projectKey = (i) => project?.key || projects.find((p) => String(p._id) === String(i.projectId))?.key;

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <TextField size="small" placeholder="Search text or key" value={text} onChange={(e) => { setText(e.target.value); setPage(0); }} sx={{ ...fieldSx, width: 230 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search size={14} /></InputAdornment> }} />
        <ToggleButtonGroup size="small" exclusive value={preset} onChange={(_, v) => { if (v) { setPreset(v); setPage(0); } }} sx={{ flexWrap: "wrap" }}>
          {[["all", "All"], ["mine", "My issues"], ["reported", "Reported by me"], ["open", "Open"], ["overdue", "Overdue"], ["recent", "Updated 7d"], ["done", "Done"]].map(([v, l]) => (
            <ToggleButton key={v} value={v} sx={{ textTransform: "none", fontSize: TYPE.micro, px: 1.25 }}>{l}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Button size="small" variant={showFilters ? "contained" : "outlined"} sx={showFilters ? primaryButtonSx : ghostButtonSx} startIcon={<Filter size={14} />} onClick={() => setShowFilters((s) => !s)}>
          Filters{conditionCount(query) ? ` (${conditionCount(query)})` : ""}
        </Button>
        <Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<Star size={14} />} onClick={(e) => setMenu(e.currentTarget)}>{activeFilter ? activeFilter.name : "Saved filters"}</Button>
        <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
          {!filters.length && <MenuItem disabled>No saved filters yet</MenuItem>}
          {filters.map((f) => (
            <MenuItem key={f._id} onClick={() => applyFilter(f)} sx={{ gap: 1 }}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); pm.starFilter(f._id, !f.starred).then(() => pm.filters(project?._id).then(setFilters)); }}><Star size={13} fill={f.starred ? "#F79009" : "none"} color={f.starred ? "#F79009" : T.inkMuted} /></IconButton>
              <span style={{ flexGrow: 1 }}>{f.name}</span>
              {f.visibility === "shared" && <Share2 size={12} color={T.inkMuted} />}
              {f.mine && <IconButton size="small" onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete filter "${f.name}"?`)) pm.deleteFilter(f._id).then(() => pm.filters(project?._id).then(setFilters)); }}><Trash2 size={13} /></IconButton>}
            </MenuItem>
          ))}
        </Menu>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<Save size={14} />} onClick={() => setSaveOpen(true)}>Save filter</Button>
        <Button size="small" startIcon={<Download size={14} />} onClick={async () => { try { saveBlob(await pm.exportIssues(project?._id, effective), `${project?.key || "issues"}-export.csv`); } catch (err) { ctx.fail(err, "Export failed"); } }}>Export</Button>
        {project && project.permissions.includes("issue.create") && !project.archivedAt && <Button size="small" startIcon={<Upload size={14} />} onClick={() => setImportOpen(true)}>Import</Button>}
      </Stack>
      {showFilters && <FilterBuilder query={query} onChange={(q) => { setQuery(q); setPage(0); }} sprints={sprints} />}
      {selected.length > 0 && <BulkBar selected={selected} clear={() => setSelected([])} onDone={load} sprints={sprints} />}
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      <GlassCard sx={{ overflowX: "auto" }}>
        {!rows ? (
          <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={22} /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox"><Checkbox size="small" checked={rows.length > 0 && selected.length === rows.length} indeterminate={selected.length > 0 && selected.length < rows.length} onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r._id) : [])} /></TableCell>
                {COLUMNS.map(([k, l]) => (
                  <TableCell key={k} sx={{ ...labelSx, whiteSpace: "nowrap" }}>
                    {["key", "priority", "points", "due", "updated", "status"].includes(k) ? (
                      <TableSortLabel active={sort.by === k} direction={sort.by === k ? sort.order : "asc"} onClick={() => setSort((s) => ({ by: k, order: s.by === k && s.order === "asc" ? "desc" : "asc" }))}>{l}</TableSortLabel>
                    ) : l}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!rows.length && <TableRow><TableCell colSpan={COLUMNS.length + 1} sx={{ textAlign: "center", py: 5, color: T.inkMuted }}>No issues match.</TableCell></TableRow>}
              {rows.map((i) => (
                <TableRow key={i._id} hover selected={selected.includes(i._id)} sx={{ cursor: "pointer" }} onClick={() => setOpenIssue(i)}>
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}><Checkbox size="small" checked={selected.includes(i._id)} onChange={(e) => setSelected((s) => (e.target.checked ? [...s, i._id] : s.filter((x) => x !== i._id)))} /></TableCell>
                  <TableCell><TypeIcon typeId={i.typeId} size={14} /></TableCell>
                  <TableCell><IssueKey issue={i} projectKey={projectKey(i)} onClick={() => setOpenIssue(i)} /></TableCell>
                  <TableCell sx={{ maxWidth: 420 }}><Typography noWrap sx={{ fontSize: TYPE.small }}>{i.summary}</Typography><Labels labels={i.labels} max={3} /></TableCell>
                  <TableCell><StatusPill statusId={i.statusId} /></TableCell>
                  <TableCell><PriorityIcon priorityId={i.priorityId} /></TableCell>
                  <TableCell><Assignee person={i.assignee} showName size={20} /></TableCell>
                  <TableCell><Points value={i.storyPoints} /></TableCell>
                  <TableCell sx={{ fontSize: TYPE.micro, whiteSpace: "nowrap", color: i.dueDate && i.statusCategory !== "done" && new Date(i.dueDate) < new Date() ? "#B42318" : T.inkSecondary }}>{i.dueDate ? formatDate(i.dueDate) : "—"}</TableCell>
                  <TableCell sx={{ fontSize: TYPE.micro, color: T.inkMuted, whiteSpace: "nowrap" }}><Tooltip title={formatDate(i.updatedAt)}><span>{formatRelative(i.updatedAt)}</span></Tooltip></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={limit} onRowsPerPageChange={(e) => { setLimit(Number(e.target.value)); setPage(0); }} rowsPerPageOptions={[25, 50, 100, 200]} />
      </GlassCard>
      <IssueDetailModal
        issueKey={openIssue?.key}
        open={Boolean(openIssue)}
        onClose={() => setOpenIssue(null)}
        onOpenIssue={(key) => setOpenIssue({ key })}
        onChanged={load}
      />
      <SaveFilterDialog open={saveOpen} onClose={() => setSaveOpen(false)} existing={activeFilter} query={{ all: effective.all, any: effective.any, none: effective.none, text: effective.text, sort }} projectId={project?._id}
        onSaved={(f) => { setActiveFilter(f); pm.filters(project?._id).then(setFilters); }} />
      {project && <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} project={project} onDone={load} />}
    </Box>
  );
};

const SaveFilterDialog = ({ open, onClose, existing, query, projectId, onSaved }) => {
  const ctx = usePm();
  const [name, setName] = useState("");
  const [visibility, setVisibility] = useState("private");
  const [scope, setScope] = useState("project");
  useEffect(() => {
    if (open) {
      setName(existing?.mine ? existing.name : "");
      setVisibility(existing?.visibility || "private");
    }
  }, [open, existing]);
  const save = async (asNew) => {
    try {
      const f = await pm.saveFilter(asNew || !existing?.mine ? null : existing._id, { name, visibility, query, projectId: projectId && scope === "project" ? projectId : null });
      ctx.notify("Filter saved");
      onSaved(f);
      onClose();
    } catch (err) {
      ctx.fail(err);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Save filter</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField size="small" label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus sx={fieldSx} />
          <Select label="Visibility" value={visibility} onChange={setVisibility} options={[{ value: "private", label: "Only me" }, { value: "shared", label: "Everyone in the workspace (with access to the issues)" }]} />
          {projectId && <Select label="Scope" value={scope} onChange={setScope} options={[{ value: "project", label: "This project" }, { value: "all", label: "All projects" }]} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {existing?.mine && <Button onClick={() => save(true)} disabled={!name}>Save as new</Button>}
        <Button variant="contained" sx={primaryButtonSx} disabled={!name} onClick={() => save(false)}>{existing?.mine ? "Update" : "Save"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default IssueNavigator;
